import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useQuery } from '@tanstack/react-query';
import type { SupportChatMessage, SupportQuickReply } from '@pcp/shared';
import { useSupportChatStore } from '../lib/stores';
import { getSettings } from '../lib/api';
import { useMediaQuery } from '../lib/useMediaQuery';
import { BottomSheet } from './BottomSheet';
import { Icon } from '../lib/icons';

/**
 * Embedded RealEquity B2B Support Chat — a floating, minimizable popover that
 * behaves like a best-in-class embedded support widget. Self-contained (its own
 * store + the /api/support-chat transport) so it can be lifted into the separate
 * B2B portal. Mounted once at the app root (App.tsx), persists across routes.
 */

const EASE = [0.16, 1, 0.3, 1] as const;

function initialsOf(name: string): string {
  const t = (name || 'S').trim();
  return (t[0] || 'S').toUpperCase();
}

/** Small round agent avatar (initial on an accent disc). */
function AgentAvatar({ name, size = 28 }: { name: string; size?: number }) {
  return (
    <span
      aria-hidden
      style={{
        width: size, height: size, borderRadius: 'var(--radius-full)', flexShrink: 0,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--accent-primary)', color: 'var(--text-inverse, #fff)',
        fontSize: size * 0.42, fontWeight: 700,
      }}
    >
      {initialsOf(name)}
    </span>
  );
}

function QuickReplies({ choices, onPick }: { choices: SupportQuickReply[]; onPick: (value: string, label: string) => void }) {
  if (!choices?.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
      {choices.map((c, i) => (
        <button
          key={`${c.label}-${i}`}
          type="button"
          className="support-quick-reply"
          onClick={() => onPick(c.value, c.label)}
          title={c.value}
        >
          {c.label}
        </button>
      ))}
    </div>
  );
}

function Sources({ sources }: { sources: NonNullable<SupportChatMessage['sources']> }) {
  if (!sources?.length) return null;
  return (
    <div style={{ marginTop: 'var(--space-2)', display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-tertiary)' }}>
        📚 Based on {sources.length} source{sources.length === 1 ? '' : 's'}
      </span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {sources.map((s, i) => {
          const inner = (
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>{s.title}</span>
          );
          const style: React.CSSProperties = {
            display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '11px',
            padding: '2px 8px', borderRadius: 'var(--radius-full)',
            background: 'var(--bg-base)', border: '1px solid var(--border-subtle)',
            color: 'var(--text-secondary)', textDecoration: 'none', maxWidth: '100%',
          };
          return s.url
            ? <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" style={{ ...style, cursor: 'pointer' }}>{inner}</a>
            : <span key={i} style={style}>{inner}</span>;
        })}
      </div>
    </div>
  );
}

function MessageBubble({ m, isLast, awaiting, onPick }: {
  m: SupportChatMessage; isLast: boolean; awaiting: boolean; onPick: (value: string, label: string) => void;
}) {
  if (m.role === 'user') {
    return <div className="chat-bubble chat-bubble-user" style={{ whiteSpace: 'pre-wrap' }}>{m.content}</div>;
  }
  return (
    <div className="chat-bubble chat-bubble-ai" style={{ maxWidth: '92%' }}>
      {m.human && (
        <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--accent-primary)', marginBottom: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span aria-hidden>🧑‍💼</span> Teammate
        </div>
      )}
      <div className="markdown-body" style={{ fontSize: 'var(--text-sm)' }}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{ a: (props) => <a {...props} target="_blank" rel="noopener noreferrer" /> }}
        >
          {m.content}
        </ReactMarkdown>
      </div>
      {m.handoff && (
        <div style={{
          marginTop: 'var(--space-2)', fontSize: 'var(--text-xs)', color: 'var(--accent-warning)',
          display: 'inline-flex', alignItems: 'center', gap: 6,
        }}>
          <span aria-hidden>🤝</span> Flagged for a human teammate
        </div>
      )}
      {m.sources && m.sources.length > 0 && <Sources sources={m.sources} />}
      {isLast && !awaiting && m.quickReplies && m.quickReplies.length > 0 && (
        <QuickReplies choices={m.quickReplies} onPick={onPick} />
      )}
    </div>
  );
}

/** Self-contained "Orbiting Constellation" — a scoped copy of the PCP
 *  AiWorkingIndicator (no shared import) so the widget stays portable. */
function SupportConstellation() {
  return (
    <span className="sc-aiw" aria-hidden>
      <i className="sc-aiw-core" />
      <span className="sc-aiw-orbit sc-aiw-orbit1"><i className="sc-aiw-sat" /></span>
      <span className="sc-aiw-orbit sc-aiw-orbit2"><i className="sc-aiw-sat" /></span>
      <i className="sc-aiw-comet" />
    </span>
  );
}

function TypingIndicator() {
  return (
    <div className="chat-bubble chat-bubble-ai" style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', padding: 'var(--space-2) var(--space-3)' }}>
      <SupportConstellation />
      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>working…</span>
    </div>
  );
}

/** Shown when a human teammate is actively composing a reply (driven by the
 *  realtime `typing` stream events). */
function TeammateTypingIndicator() {
  return (
    <div className="chat-bubble chat-bubble-ai" style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', padding: 'var(--space-2) var(--space-3)' }}>
      <span style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--accent-primary)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <span aria-hidden>🧑‍💼</span> Teammate
      </span>
      <span className="typing-dots" aria-label="is typing"><i /><i /><i /></span>
    </div>
  );
}

/** Refresh/clear-session control with a small confirm popover. Clears + starts a
 *  fresh session in place (does NOT minimize). */
function ClearWithConfirm({ onConfirm }: { onConfirm: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        className="btn-icon support-header-btn"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        title="Clear chat & start over"
        aria-label="Clear chat & start over"
      >
        <Icon name="refresh" size={16} />
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 1 }} />
          <div style={{
            position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 2, width: 220,
            background: 'var(--bg-elevated)', border: '1px solid var(--border-strong)',
            borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-xl)', padding: 'var(--space-3)',
          }}>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginBottom: 'var(--space-2)', lineHeight: 1.5 }}>
              Clear this chat and start over? This conversation will be gone.
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setOpen(false)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={() => { setOpen(false); onConfirm(); }}>Clear</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/** Small "End conversation" control with a confirm popover (customer-initiated close). */
function EndChatControl({ onEnd }: { onEnd: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        onClick={() => setOpen((o) => !o)}
        style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}
      >
        End conversation
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 1 }} />
          <div style={{
            position: 'absolute', bottom: 'calc(100% + 6px)', right: 0, zIndex: 2, width: 230,
            background: 'var(--bg-elevated)', border: '1px solid var(--border-strong)',
            borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-xl)', padding: 'var(--space-3)',
          }}>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginBottom: 'var(--space-2)', lineHeight: 1.5 }}>
              End this conversation? You can always start a new one.
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setOpen(false)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={() => { setOpen(false); onEnd(); }}>End chat</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/** The conversation thread + composer — shared by the desktop dock and the mobile sheet. */
function SupportThread() {
  const { messages, awaiting, status, humanHandling, agentTyping, send, notifyTyping, endConversation, startFresh } = useSupportChatStore();
  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, awaiting, agentTyping]);

  const submit = () => {
    const t = draft.trim();
    if (!t || awaiting) return;
    setDraft('');
    void send(t);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
      <div
        ref={scrollRef}
        className="chat-messages"
        style={{ padding: 'var(--space-3)', gap: 'var(--space-3)', flex: 1, overflowY: 'auto' }}
      >
        {messages.map((m, i) => (
          <MessageBubble
            key={m.id}
            m={m}
            isLast={i === messages.length - 1}
            awaiting={awaiting}
            onPick={(value, label) => { if (!awaiting) void send(value, label); }}
          />
        ))}
        {awaiting && <TypingIndicator />}
        {!awaiting && agentTyping && <TeammateTypingIndicator />}
      </div>

      {status === 'closed' ? (
        <div style={{
          padding: 'var(--space-4) var(--space-3)', borderTop: '1px solid var(--border-subtle)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-2)', textAlign: 'center',
        }}>
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
            This conversation has ended. Thanks for chatting!
          </span>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => void startFresh()}>
            Start a new chat
          </button>
        </div>
      ) : (
        <>
          {humanHandling ? (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: 'var(--space-2) var(--space-3)', margin: '0 var(--space-3)',
              background: 'var(--accent-primary-soft)', color: 'var(--accent-primary)',
              borderRadius: 'var(--radius-md)', fontSize: 'var(--text-xs)', fontWeight: 600,
            }}>
              <span aria-hidden>🧑‍💼</span> You're now chatting with a teammate.
            </div>
          ) : status === 'escalated' && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: 'var(--space-2) var(--space-3)', margin: '0 var(--space-3)',
              background: 'var(--accent-success-soft)', color: 'var(--accent-success)',
              borderRadius: 'var(--radius-md)', fontSize: 'var(--text-xs)', fontWeight: 600,
            }}>
              <span aria-hidden>✓</span> Your request has been sent to our team — we'll be in touch.
            </div>
          )}

          <div className="chat-input-area" style={{ padding: 'var(--space-3)', borderTop: '1px solid var(--border-subtle)', margin: 0 }}>
            <textarea
              className="chat-input"
              value={draft}
              onChange={(e) => { setDraft(e.target.value); notifyTyping(); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); }
              }}
              placeholder="Type your message…"
              rows={1}
              style={{ minHeight: 44 }}
            />
            <button
              type="button"
              className="chat-send-btn"
              onClick={submit}
              disabled={!draft.trim() || awaiting}
              aria-label="Send message"
              style={{ opacity: (!draft.trim() || awaiting) ? 0.5 : 1 }}
            >
              <Icon name="send" size={18} />
            </button>
          </div>
          {/* Customer-initiated end — close the thread when no more help is needed. */}
          <div style={{ display: 'flex', justifyContent: 'center', padding: '0 var(--space-3) var(--space-3)' }}>
            <EndChatControl onEnd={() => void endConversation()} />
          </div>
        </>
      )}
    </div>
  );
}

function PanelHeader({ agentName, agentTitle, onMinimize, onClear }: {
  agentName: string; agentTitle: string; onMinimize: () => void; onClear: () => void;
}) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
      padding: 'var(--space-3)', borderBottom: '1px solid var(--border-subtle)',
      background: 'var(--accent-primary-soft)',
    }}>
      <AgentAvatar name={agentName} size={32} />
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
        <span style={{ fontWeight: 700, fontSize: 'var(--text-sm)', lineHeight: 1.2 }}>{agentName}</span>
        <span style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span className="status-dot status-dot-connected" /> {agentTitle}
        </span>
      </div>
      {/* Clear session (refresh + confirm popover) on the left; minimize (chevron) on the right. */}
      <ClearWithConfirm onConfirm={onClear} />
      <button type="button" className="btn-icon support-header-btn" onClick={onMinimize} title="Minimize" aria-label="Minimize">
        <Icon name="chevronDown" size={18} />
      </button>
    </div>
  );
}

export function SupportChatWidget() {
  const { open, started, conversationId, agentName, agentTitle, minimize, startFresh } = useSupportChatStore();
  const isCompact = useMediaQuery('(max-width: 640px)');

  // Respect the master enable switch (hide the launcher entirely when off).
  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: getSettings, staleTime: 60_000 });
  const enabled = (settings as any)?.support?.enabled !== false;

  // Realtime delivery: open an SSE stream while a conversation is live (even when
  // minimized, so the unread badge updates instantly). A slow fallback poll covers
  // any SSE gap (proxy hiccup, reconnect window) and is harmless thanks to id dedup.
  useEffect(() => {
    if (!started || !conversationId) return;
    const store = useSupportChatStore.getState();
    store.connectStream();
    const t = setInterval(() => { void useSupportChatStore.getState().sync(); }, 20_000);
    return () => {
      clearInterval(t);
      useSupportChatStore.getState().disconnectStream();
    };
  }, [started, conversationId]);

  if (!enabled) return null;

  // Mobile: the expanded chat fills the screen via the mandated BottomSheet.
  // The sheet supplies its own header (title + ✕ = minimize); we add a slim row
  // for the clear-session (refresh + confirm) control so it's reachable on mobile.
  const mobilePanel = (
    <BottomSheet
      open={open}
      onClose={minimize}
      title={`${agentName} · ${agentTitle}`}
      size="full"
      padded={false}
      footer={undefined}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '4px var(--space-2)', borderBottom: '1px solid var(--border-subtle)' }}>
          <ClearWithConfirm onConfirm={() => void startFresh()} />
        </div>
        <SupportThread />
      </div>
    </BottomSheet>
  );

  const desktopPanel = (
    <AnimatePresence>
      {open && (
        <motion.div
          key="support-panel"
          className="support-panel glass-card-static"
          initial={{ opacity: 0, scale: 0.92, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: 12 }}
          transition={{ duration: 0.2, ease: EASE }}
          style={{ transformOrigin: 'bottom right' }}
          role="dialog"
          aria-label={`${agentTitle} chat`}
        >
          <PanelHeader agentName={agentName} agentTitle={agentTitle} onMinimize={minimize} onClear={() => void startFresh()} />
          <SupportThread />
        </motion.div>
      )}
    </AnimatePresence>
  );

  // The launcher now lives as an icon in the sidebar action row (Sidebar.tsx),
  // so the panel just opens/closes in place — no floating corner bubble.
  return <>{isCompact ? mobilePanel : desktopPanel}</>;
}
